/**
 * Phase 5 — compliance canonical constants.
 *
 * These literal lists power both Postgres enums and Zod parsers across
 * the API, worker, and web app. Adding or removing a value requires a
 * coordinated migration; the lists are the single source of truth for
 * the cross-process contract.
 */

export const COMPLIANCE_CADENCES = [
  'weekly',
  'monthly',
  'quarterly',
  'semi_annually',
  'annually',
  'biennially',
  'custom',
] as const;
export type ComplianceCadence = (typeof COMPLIANCE_CADENCES)[number];

export const COMPLIANCE_CADENCE_LABELS: Record<ComplianceCadence, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semi-annually',
  annually: 'Annually',
  biennially: 'Biennially',
  custom: 'Custom',
};

export const COMPLIANCE_RECORD_STATUSES = [
  'upcoming',
  'due_soon',
  'overdue',
  'logged',
  'skipped',
] as const;
export type ComplianceRecordStatus = (typeof COMPLIANCE_RECORD_STATUSES)[number];

export const COMPLIANCE_RECORD_STATUS_LABELS: Record<ComplianceRecordStatus, string> = {
  upcoming: 'Upcoming',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  logged: 'Logged',
  skipped: 'Skipped',
};

export const COMPLIANCE_LOG_OUTCOMES = [
  'passed',
  'passed_with_notes',
  'failed',
  'requires_followup',
] as const;
export type ComplianceLogOutcome = (typeof COMPLIANCE_LOG_OUTCOMES)[number];

export const COMPLIANCE_LOG_OUTCOME_LABELS: Record<ComplianceLogOutcome, string> = {
  passed: 'Passed',
  passed_with_notes: 'Passed with notes',
  failed: 'Failed',
  requires_followup: 'Requires follow-up',
};

export const COMPLIANCE_LOG_ACTOR_KINDS = ['staff', 'vendor', 'system'] as const;
export type ComplianceLogActorKind = (typeof COMPLIANCE_LOG_ACTOR_KINDS)[number];

export const COMPLIANCE_REMINDER_STAGES = [
  'thirty_day',
  'fourteen_day',
  'seven_day',
  'one_day',
  'overdue',
] as const;
export type ComplianceReminderStage = (typeof COMPLIANCE_REMINDER_STAGES)[number];

/** Days-before-due threshold for each reminder stage. The `overdue`
 *  stage fires when the grace period has expired, not on a fixed
 *  day-count, so it carries `0` here for completeness. */
export const COMPLIANCE_REMINDER_DAYS: Record<ComplianceReminderStage, number> = {
  thirty_day: 30,
  fourteen_day: 14,
  seven_day: 7,
  one_day: 1,
  overdue: 0,
};

/** Polymorphic `owner_kind` for the (now-shared) attachments table.
 *  Phase 4 added `inspection_answer` and `inspection_pdf`. */
export const ATTACHMENT_OWNER_KINDS = [
  'work_order',
  'compliance_log',
  'device',
  'inspection_answer',
  'inspection_pdf',
] as const;
export type AttachmentOwnerKind = (typeof ATTACHMENT_OWNER_KINDS)[number];

/** Approximate interval-in-days for cadence math. Used for the
 *  rolling-forward of `next_due_at` after a log; the calendar-aware
 *  helper below uses these as defaults but advances actual calendar
 *  units (e.g. `annually` ≡ +1 year) so leap years and month-end
 *  drift stay correct. */
export const CADENCE_DAYS: Record<Exclude<ComplianceCadence, 'custom'>, number> = {
  weekly: 7,
  monthly: 30,
  quarterly: 91,
  semi_annually: 183,
  annually: 365,
  biennially: 730,
};

export interface CadenceSpec {
  cadence: ComplianceCadence;
  customIntervalDays: number | null;
}

/** Returns the resolved interval in days. For calendar-aware cadences
 *  (`monthly` and longer) prefer `advanceByCadence` to avoid drift. */
export function cadenceIntervalDays(spec: CadenceSpec): number {
  if (spec.cadence === 'custom') {
    if (!spec.customIntervalDays || spec.customIntervalDays <= 0) {
      throw new Error('custom cadence requires a positive customIntervalDays');
    }
    return spec.customIntervalDays;
  }
  return CADENCE_DAYS[spec.cadence];
}

/** Advance an anchor timestamp by one cadence period. Calendar-aware
 *  for monthly/quarterly/semi-annual/annual/biennial; day-based for
 *  weekly and custom. Returns a new `Date` (anchor is not mutated). */
export function advanceByCadence(anchor: Date, spec: CadenceSpec): Date {
  const next = new Date(anchor.getTime());
  switch (spec.cadence) {
    case 'weekly':
      next.setUTCDate(next.getUTCDate() + 7);
      return next;
    case 'monthly':
      next.setUTCMonth(next.getUTCMonth() + 1);
      return next;
    case 'quarterly':
      next.setUTCMonth(next.getUTCMonth() + 3);
      return next;
    case 'semi_annually':
      next.setUTCMonth(next.getUTCMonth() + 6);
      return next;
    case 'annually':
      next.setUTCFullYear(next.getUTCFullYear() + 1);
      return next;
    case 'biennially':
      next.setUTCFullYear(next.getUTCFullYear() + 2);
      return next;
    case 'custom': {
      const days = cadenceIntervalDays(spec);
      next.setUTCDate(next.getUTCDate() + days);
      return next;
    }
  }
}

/** Compute a compliance "tone" from a record's status + due date. The
 *  mapping is canonical (per `DESIGN_SYSTEM.md` §9) and shared by the
 *  API dashboard rollup, the worker logger, and the web UI badge. */
export type ComplianceTone = 'critical' | 'high' | 'medium' | 'success' | 'muted';

export function complianceTone(args: {
  status: ComplianceRecordStatus;
  dueAt: Date | string;
  now?: Date;
}): ComplianceTone {
  if (args.status === 'logged') return 'success';
  if (args.status === 'skipped') return 'muted';
  if (args.status === 'overdue') return 'critical';
  const now = args.now ?? new Date();
  const dueAt = args.dueAt instanceof Date ? args.dueAt : new Date(args.dueAt);
  const msUntil = dueAt.getTime() - now.getTime();
  const daysUntil = msUntil / (24 * 60 * 60 * 1000);
  if (daysUntil < 0) return 'critical';
  if (daysUntil <= 7) return 'high';
  if (daysUntil <= 30) return 'medium';
  return 'success';
}

/** Seed defaults applied to every new org. Slugs are stable so the
 *  re-seed endpoint (`POST /device-types/seed-defaults`) can upsert
 *  by `(org_id, slug)` idempotently. */
export interface DeviceTypeSeed {
  slug: string;
  name: string;
  isComplianceTracked: boolean;
  defaultCadence: ComplianceCadence | null;
  defaultCustomIntervalDays: number | null;
  defaultGracePeriodDays: number;
  requiresUnit: boolean;
  icon: string | null;
  regulatoryNotes: string | null;
}

export const DEFAULT_DEVICE_TYPE_SEEDS: readonly DeviceTypeSeed[] = [
  {
    slug: 'elevator',
    name: 'Elevator',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'ArrowUpDown',
    regulatoryNotes: 'NYC DOB annual elevator inspection (Category 1/5).',
  },
  {
    slug: 'boiler',
    name: 'Boiler',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Flame',
    regulatoryNotes: 'NYC DOB annual boiler inspection (LL62).',
  },
  {
    slug: 'sprinkler-system',
    name: 'Sprinkler System',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Droplets',
    regulatoryNotes: 'NYC FDNY annual sprinkler inspection.',
  },
  {
    slug: 'rpz-backflow-preventer',
    name: 'RPZ Backflow Preventer',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'GitMerge',
    regulatoryNotes: 'NYC DEP annual RPZ backflow test.',
  },
  {
    slug: 'fire-extinguisher',
    name: 'Fire Extinguisher',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Flame',
    regulatoryNotes: 'Annual servicing per NFPA 10.',
  },
  {
    slug: 'standpipe-system',
    name: 'Standpipe System',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'PipetteIcon',
    regulatoryNotes: 'NYC FDNY annual standpipe inspection.',
  },
  {
    slug: 'emergency-exit-lighting',
    name: 'Emergency / Exit Lighting',
    isComplianceTracked: true,
    defaultCadence: 'monthly',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Lightbulb',
    regulatoryNotes: 'Monthly functional test per NFPA 101.',
  },
  {
    slug: 'smoke-detector',
    name: 'Smoke Detector',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: true,
    icon: 'BellRing',
    regulatoryNotes: 'NYC HPD annual smoke detector certification.',
  },
  {
    slug: 'carbon-monoxide-detector',
    name: 'Carbon Monoxide Detector',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: true,
    icon: 'BellRing',
    regulatoryNotes: 'NYC HPD annual CO detector certification.',
  },
  {
    slug: 'heating-system-pre-season',
    name: 'Heating System Pre-Season Check',
    isComplianceTracked: true,
    defaultCadence: 'annually',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Thermometer',
    regulatoryNotes: 'Pre-heat-season inspection (Oct 1 readiness).',
  },
  {
    slug: 'pest-control-service',
    name: 'Pest Control Service Log',
    isComplianceTracked: true,
    defaultCadence: 'weekly',
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 7,
    requiresUnit: false,
    icon: 'Bug',
    regulatoryNotes: 'Recurring extermination visit log.',
  },
  {
    slug: 'refrigerator',
    name: 'Refrigerator',
    isComplianceTracked: false,
    defaultCadence: null,
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: true,
    icon: 'Refrigerator',
    regulatoryNotes: null,
  },
  {
    slug: 'washer-dryer',
    name: 'Washer/Dryer',
    isComplianceTracked: false,
    defaultCadence: null,
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'WashingMachine',
    regulatoryNotes: null,
  },
  {
    slug: 'hvac-unit-non-regulated',
    name: 'HVAC Unit (non-regulated)',
    isComplianceTracked: false,
    defaultCadence: null,
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Wind',
    regulatoryNotes: null,
  },
  {
    slug: 'general-equipment',
    name: 'General Equipment',
    isComplianceTracked: false,
    defaultCadence: null,
    defaultCustomIntervalDays: null,
    defaultGracePeriodDays: 0,
    requiresUnit: false,
    icon: 'Wrench',
    regulatoryNotes: null,
  },
];
